/**
 * Feature-toggle gates consumed by the POS web app (#226).
 *
 * The profile API returns ``features: string[]`` - the V0060 ltree
 * feature_paths enabled at the active membership's location. The
 * sidebar nav, page entry guards, and any in-page gated surface read
 * from this list rather than asking the backend per-feature.
 *
 * Cascade semantics: a feature_path is enabled iff IT or one of its
 * ancestors is in the set AND no nearer-scope OFF row negated it.
 * The backend already resolved the cascade before populating the
 * array, so the frontend only needs membership tests.
 *
 * Top-level paths declared as constants here so call sites stay
 * grep-able (``isFeatureEnabled(features, INVENTORY_SEARCH)``) and a
 * future rename in the catalog is one-edit instead of N call-site
 * fixes.
 */

/** Feature paths the POS UI gates on. Keep in sync with the V0060
 *  ``feature_tbl`` seed data in tilt-runtime. */
export const FEATURE_INVENTORY = "pos.inventory";
export const FEATURE_INVENTORY_SEARCH = "pos.inventory.search";
export const FEATURE_REPORTING = "pos.reporting";
export const FEATURE_PAYMENTS = "pos.payments";
export const FEATURE_PAYMENTS_CASH = "pos.payments.cash";
export const FEATURE_PAYMENTS_CHECK = "pos.payments.check";
export const FEATURE_PAYMENTS_ACH = "pos.payments.ach";
export const FEATURE_PAYMENTS_CARD_PRESENT = "pos.payments.card_present";
export const FEATURE_PAYMENTS_VIRTUAL = "pos.payments.virtual";
export const FEATURE_PAYMENTS_PAY_BY_TEXT = "pos.payments.pay_by_text";

/**
 * Membership test against the feature set.
 *
 * The profile carries the EFFECTIVE leaves. Asking
 * ``isFeatureEnabled(features, "pos.inventory")`` should still return
 * true when ``"pos.inventory.search"`` is in the array, because the
 * parent capability is enabled iff ANY of its sub-features is.
 *
 * We don't walk upward (".inventory.search" → ".inventory") because
 * the backend already resolved the cascade - but a caller asking for
 * a parent path should match when any descendant is present. That's
 * the prefix check below.
 */
export function isFeatureEnabled(features: readonly string[] | undefined, path: string): boolean {
  if (!features || features.length === 0) return false;
  if (features.includes(path)) return true;
  const prefix = `${path}.`;
  return features.some((f) => f.startsWith(prefix));
}
